import type { Point } from "./types";
import { LEVELS, MAP_W, MAP_H } from "./levels";
import { state, matrixFor } from "./state";
import { crossingLegs, leagues } from "./geometry";
import { makeDecor, makeHarbor, makePort, makeShip, svgEl } from "./art";
import { seaEl, stageEl } from "../dom";

/**
 * Chart orientation. On narrow portrait screens (phones) the same map is
 * drawn rotated 90° into a 620×1000 viewBox, so it fills the tall screen and
 * everything renders ~60% larger — the *instance* (points, distances, proven
 * optima) is untouched; only the rendering rotates. The artwork additionally
 * gets a modest scale-up so islands and the ship stay readable.
 */
export function portraitMode(): boolean {
  return typeof window !== "undefined" &&
    window.matchMedia("(orientation: portrait) and (max-width: 700px)").matches;
}

const PORTRAIT_ART_SCALE = 1.18;

/** Orientation the live board was last built with (minis follow it too). */
let boardPortrait = false;

function chartW(portrait: boolean): number { return portrait ? MAP_H : MAP_W; }
function chartH(portrait: boolean): number { return portrait ? MAP_W : MAP_H; }

/** Map a level point into chart coordinates (rotated 90° cw in portrait). */
function mapPt(portrait: boolean, [x, y]: Point): [number, number] {
  return portrait ? [MAP_H - y, x] : [x, y];
}

/** The current live-board mapping, for callers outside this module. */
export function chartPoint(p: Point): [number, number] {
  return mapPt(boardPortrait, p);
}

/** Port groups of the live board, indexed by node index (0 = harbor). */
export const portEls: SVGGElement[] = [];

let routeLayer: SVGGElement | null = null;
let previewLayer: SVGGElement | null = null;
let shipEl: SVGGElement | null = null;
let homeTimer: number | null = null;

/* ---------- Build the live board for the current level ---------- */
export function buildBoard(): void {
  const L = state.level();
  const portrait = portraitMode();
  boardPortrait = portrait;
  const w = chartW(portrait), h = chartH(portrait);
  const k = portrait ? PORTRAIT_ART_SCALE : 1;

  seaEl.setAttribute("viewBox", `0 0 ${w} ${h}`);
  seaEl.classList.toggle("portrait", portrait);
  stageEl.style.aspectRatio = `${w} / ${h}`;
  seaEl.innerHTML = "";
  portEls.length = 0;
  if (homeTimer !== null) { clearTimeout(homeTimer); homeTimer = null; }

  seaEl.appendChild(makeDecor(w, h));

  routeLayer = svgEl("g", { class: "route-layer" });
  previewLayer = svgEl("g", { class: "preview-layer" });
  seaEl.appendChild(routeLayer);
  seaEl.appendChild(previewLayer);

  const ports = svgEl("g", { class: "ports-layer" });
  L.points.forEach((p, i) => {
    const [x, y] = mapPt(portrait, p);
    const el = i === 0 ? makeHarbor(x, y, true, k) : makePort(i, x, y, true, k);
    ports.appendChild(el);
    portEls.push(el);
  });
  seaEl.appendChild(ports);

  shipEl = makeShip(k);
  seaEl.appendChild(shipEl);
}

/* ---------- Route rendering ---------- */

/** Append the legs of `order` (a node sequence) to `layer`; when `closed`,
 *  the final leg back to order[0] is drawn in the "homeward" style. Legs in
 *  `crossed` get the warning tint. */
function drawLegs(
  layer: SVGGElement,
  points: Point[],
  order: number[],
  closed: boolean,
  crossed: Set<number>,
  portrait: boolean,
): void {
  const legCount = closed ? order.length : order.length - 1;
  for (let i = 0; i < legCount; i++) {
    const [x1, y1] = mapPt(portrait, points[order[i]]);
    const [x2, y2] = mapPt(portrait, points[order[(i + 1) % order.length]]);
    const cls =
      "leg" +
      (closed && i === order.length - 1 ? " close" : "") +
      (crossed.has(i) ? " crossed" : "");
    layer.appendChild(svgEl("line", { class: cls, x1, y1, x2, y2 }));
  }
}

/** Position + steer the ship at the end of `order` (flips to face travel). */
function moveShip(
  ship: SVGGElement,
  points: Point[],
  order: number[],
  atHome: boolean,
  portrait: boolean,
  artScale: number,
): void {
  const [lx, ly] = mapPt(portrait, points[order[order.length - 1]]);
  let flip = 1;
  if (order.length > 1) {
    const [px] = mapPt(portrait, points[order[order.length - 2]]);
    if (lx < px) flip = -1;
  }
  // moor just off the island, on the water side
  ship.setAttribute("transform", `translate(${lx + 2} ${ly + 27 * artScale}) scale(${flip} 1)`);
  ship.classList.toggle("home", atHome);
}

/** Re-render the player's route, badges, port states, and the ship. */
export function renderRoute(): void {
  if (!routeLayer || !shipEl) return;
  const L = state.level();
  const order = [0, ...state.route];
  const complete = state.route.length === state.portCount();
  const portrait = boardPortrait;
  const k = portrait ? PORTRAIT_ART_SCALE : 1;

  routeLayer.innerHTML = "";
  const crossed = complete ? crossingLegs(order, L.points) : new Set<number>();
  drawLegs(routeLayer, L.points, order, complete, crossed, portrait);

  // port visit states + badges
  const posInRoute = new Map<number, number>();
  state.route.forEach((p, i) => posInRoute.set(p, i + 1));
  portEls.forEach((el, idx) => {
    if (idx === 0) {
      el.classList.toggle("route-done", complete);
      return;
    }
    const visited = posInRoute.has(idx);
    el.classList.toggle("visited", visited);
    const num = el.querySelector(".badge-num");
    if (num) num.textContent = visited ? String(posInRoute.get(idx)) : "";
  });

  // sail to the route's end; when the round just closed, visit the final
  // island first and only then take the homeward leg (two animated steps)
  if (homeTimer !== null) { clearTimeout(homeTimer); homeTimer = null; }
  moveShip(shipEl, L.points, order, false, portrait, k);
  if (complete) {
    const ship = shipEl;
    homeTimer = window.setTimeout(() => {
      homeTimer = null;
      moveShip(ship, L.points, [...order, 0], true, portrait, k);
    }, 640); // a touch longer than the ship's CSS transform transition
  }
}

/** True if the current route on the board crosses itself (only meaningful
 *  when complete; used for the honest "this can be shorter" hint). */
export function routeHasCrossing(): boolean {
  const order = [0, ...state.route];
  if (state.route.length !== state.portCount()) return false;
  return crossingLegs(order, state.level().points).size > 0;
}

/* ---------- Hover preview (the dashed "what if" leg) ---------- */
export function clearPreview(): void {
  if (previewLayer) previewLayer.innerHTML = "";
}

/** Show a ghost leg from the route's current end to port `idx`, with the
 *  extra distance it would add. */
export function showPreview(idx: number): void {
  if (!previewLayer) return;
  clearPreview();
  const L = state.level();
  const cur = state.route.length ? state.route[state.route.length - 1] : 0;
  if (idx === cur) return;
  const [x1, y1] = mapPt(boardPortrait, L.points[cur]);
  const [x2, y2] = mapPt(boardPortrait, L.points[idx]);
  previewLayer.appendChild(svgEl("line", { class: "ghost-leg", x1, y1, x2, y2 }));

  const d = matrixFor(state.idx)[cur][idx];
  const label = svgEl("text", {
    class: "ghost-label",
    x: (x1 + x2) / 2,
    y: (y1 + y2) / 2 - 10,
    "text-anchor": "middle",
  });
  label.textContent = `+${leagues(d)} lg`;
  previewLayer.appendChild(label);
}

/* ---------- Mini charts (for the reveal) ---------- */

/**
 * Render a complete mini chart into `host`: the level's sea, ports, and the
 * given route (node order, leading 0 included). `closed` draws the homeward
 * leg; partial orders (during the reveal animation) show the ship under way.
 * Follows the live board's orientation, so phone minis are portrait too.
 */
export function buildMiniChart(
  host: HTMLElement,
  levelIdx: number,
  order: number[],
  closed: boolean,
): void {
  const L = LEVELS[levelIdx];
  const portrait = portraitMode();
  const w = chartW(portrait), h = chartH(portrait);
  host.innerHTML = "";
  const svg = svgEl("svg", { class: "sea mini", viewBox: `0 0 ${w} ${h}` });
  if (portrait) svg.classList.add("portrait");

  svg.appendChild(makeDecor(w, h));

  const routes = svgEl("g", { class: "route-layer" });
  const crossed = closed ? crossingLegs(order, L.points) : new Set<number>();
  drawLegs(routes, L.points, order, closed, crossed, portrait);
  svg.appendChild(routes);

  const ports = svgEl("g", { class: "ports-layer" });
  const posInRoute = new Map<number, number>();
  order.forEach((p, i) => { if (p !== 0) posInRoute.set(p, i); });
  L.points.forEach((p, i) => {
    const [x, y] = mapPt(portrait, p);
    const el = i === 0 ? makeHarbor(x, y, false) : makePort(i, x, y, false);
    if (i !== 0 && posInRoute.has(i)) {
      el.classList.add("visited");
      const num = el.querySelector(".badge-num");
      if (num) num.textContent = String(posInRoute.get(i));
    }
    ports.appendChild(el);
  });
  svg.appendChild(ports);

  if (order.length > 1) {
    const ship = makeShip();
    moveShip(ship, L.points, closed ? [...order, 0] : order, closed, portrait, 1);
    svg.appendChild(ship);
  }

  host.appendChild(svg);
}
